declare const isoDateStringSymbol: unique symbol
export type ISODateString = string & { [isoDateStringSymbol]: never }

export type ConditionLineStatus = 'new' | 'updated' | 'existing' | 'none'
export type DiffLabel = 'NEW' | 'UPDATED' | 'EXISTING' | 'OUTDATED' | null

export enum BulletType {
  Assessment = 'ASSESSMENT',
  General = 'GENERAL',
  Plan = 'PLAN',
}

// maps to constants in automed/assessment_play.py
export enum BulletTag {
  Info = 'info',
  Medication = 'medication',
  Observation = 'observation',
}

// maps to Interpretation constants in automed/models/ranges.py
export enum ObservationInterpretation {
  Critical = 'critical',
  CriticallyHigh = 'critically high',
  CriticallyLow = 'critically low',
  High = 'high',
  Low = 'low',
  Normal = 'normal',
  QualitativelyAbnormal = 'abnormal',
  Unknown = '',
}
export type ObservationInterpretationLiteral = `${ObservationInterpretation}`

export enum BulletNoteStatus {
  Hidden = 'hidden',
  Monitored = 'monitored',
  Noted = 'noted',
  Shelved = 'shelved',
}

export enum NoteType {
  Blank = 'blank', // no DB note available, or empty note
  Draft = 'draft', // a draft stored in `generated_notes` DB table
  Note = 'note', // a note parsed from a signed EHR note
}

export enum SourceType {
  SourceCondition = 'SourceCondition',
  SourceNote = 'SourceNote',
  SupportingEvidence = 'SupportingEvidence',
}

export enum DiagnosisType {
  CoreDx = 'Core Dx', // best
  LiveDx = 'Live Dx', // good
  SupplementalDx = 'Supplemental Dx', // better
}
export type DiagnosisTypes = `${DiagnosisType}`

export enum ConditionDevelopmentState {
  Beta = 'Beta',
  CaseReview = 'Under Case Review',
  InProgress = 'Work In Progress',
  ProdReady = 'Prod Ready',
  Unknown = 'Unknown',
}
export type ConditionDevelopmentStates = `${ConditionDevelopmentState}`

export enum PatientLoadPhase {
  Started = 'STARTED',
  Fetching = 'FETCHING',
  Mapping = 'MAPPING',
  Dxing = 'DXING',
  Loading = 'LOADING', // Fallback status indicating that our tracking of load phase has failed
  Meshing1 = 'MESHING_1',
  Meshing2 = 'MESHING_2',
  Meshing3 = 'MESHING_3',
}
export type PatientLoadPhases = `${PatientLoadPhase}`

export enum PatientLoadDuration {
  Long = 'LONG', // Initial patient load
  Medium = 'MEDIUM', // Patient load after 5 days
  Short = 'SHORT', // Near "instant" patient load
}
export type PatientLoadDurations = `${PatientLoadDuration}`

export enum HtmlVersion {
  // PlainText = 0, // (deprecated) No Html tags or formatting
  // HtmlWithBold = 1, // (deprecated) Html with support for <p> and <strong> tags
  // HtmlWithDiv = 2, // (deprecated) Html with support for <div> and <strong> tags
  HtmlWithEMU = 3, // Html with support for <div>, <strong>, <em>, and <u> tags
}

export enum ConditionLineType {
  Bullet = 'bullet',
  FreeText = 'freetext',
  ShelfDivider = 'shelfDivider',
  Title = 'title',
}
export type ConditionLineTypeLiteral = `${ConditionLineType}`

export type FetchError = {
  message?: string
  redirect?: string | null
  status: number | string
}

export enum NotificationType {
  BaseNoteChanged = 'base-note-changed',
  ConditionsDismissed = 'conditions-dismissed',
  ConditionsUpdated = 'conditions-updated',
  CriticalDiagnosis = 'critical-diagnosis',
  NoDiagnosis = 'no-diagnosis',
  NoteError = 'note-error',
  PreviousEncounter = 'previous-encounter',
}
export type NotificationTypeLiteral = `${NotificationType}`
